import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

/**
 * CatalogSelect — unified dropdown pulling from the CRM catalog with
 * admin-gated Quick-Add + duplicate-check behaviour.
 *
 * Props:
 *   value               string — currently selected value
 *   onChange            fn(newValue)
 *   type                'program' | 'country' | 'university' | 'intake'
 *   placeholder?        string
 *   disabled?           boolean
 *   testId?             string — data-testid for the trigger
 *   allowQuickAdd?      boolean (default: true) — hide + button entirely if false
 *   user?               { role } — used to gate Quick-Add to allowed roles
 *   onCatalogUpdate?    fn() — optional callback after a new value is added
 */
const META = {
  program: {
    label: 'program',
    fetchKey: 'programs',
    listEndpoint: '/constants',
    addEndpoint: '/constants/programs',
    addField: 'program',
    allowedRoles: ['admin', 'counselor', 'operating_head', 'team_leader'],
  },
  country: {
    label: 'country',
    fetchKey: 'countries',
    listEndpoint: '/constants',
    // No add endpoint — countries come from a locked hardcoded master list to prevent
    // duplicates like "GEORGIA" / "Georgia" / "georgia" from manual entry.
    allowQuickAddOverride: false,
    allowedRoles: [],
  },
  university: {
    label: 'university',
    fetchKey: 'universities',
    listEndpoint: '/constants',
    addEndpoint: '/constants/universities',
    addField: 'university',
    allowedRoles: ['admin', 'counselor', 'operating_head', 'team_leader'],
  },
  payment_title: {
    label: 'payment title',
    fetchKey: 'payment_titles',
    listEndpoint: '/constants',
    addEndpoint: '/constants/payment-titles',
    addField: 'payment_title',
    allowedRoles: ['admin', 'operating_head', 'accountant', 'documentation_team'],
  },
  payment_due_label: {
    label: 'due label',
    fetchKey: 'payment_due_labels',
    listEndpoint: '/constants',
    addEndpoint: '/constants/payment-due-labels',
    addField: 'payment_due_label',
    allowedRoles: ['admin', 'operating_head', 'accountant', 'documentation_team'],
  },
  intake: {
    label: 'intake',
    listEndpoint: '/system-settings/intakes/selectable',
    // intakes are added via Settings > Intakes, no quick add (avoids polluting with ad-hoc intakes)
    allowQuickAddOverride: false,
    allowedRoles: ['admin', 'operating_head'],
  },
};

export default function CatalogSelect({
  value,
  onChange,
  type,
  placeholder,
  disabled,
  testId,
  allowQuickAdd = true,
  user,
  onCatalogUpdate,
}) {
  const meta = META[type];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [quickInput, setQuickInput] = useState('');
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const canQuickAdd =
    (allowQuickAdd && meta.allowQuickAddOverride !== false) &&
    !!meta.addEndpoint &&
    (user && meta.allowedRoles.includes(user.role));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(meta.listEndpoint);
      if (type === 'intake') {
        setItems((res.data?.intakes || []).map(i => i.name || i.id));
      } else {
        setItems(res.data?.[meta.fetchKey] || []);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [meta.listEndpoint, meta.fetchKey, type]);

  useEffect(() => {
    load();
  }, [load]);

  const handleQuickAdd = async () => {
    const clean = quickInput.trim();
    if (!clean) {
      toast.error(`Enter a ${meta.label} name`);
      return;
    }
    // Client-side duplicate check (case-insensitive)
    const exists = items.find(i => i.toLowerCase() === clean.toLowerCase());
    if (exists) {
      toast.info(`${exists} already exists in the catalog`);
      onChange(exists);
      setShowQuickAdd(false);
      setQuickInput('');
      return;
    }
    setAdding(true);
    try {
      const body = { [meta.addField]: clean };
      const res = await apiClient.post(meta.addEndpoint, body);
      const finalName = res.data?.[meta.addField] || clean;
      await load();
      onChange(finalName);
      if (res.data?.message === `${meta.label.charAt(0).toUpperCase() + meta.label.slice(1)} already exists`) {
        toast.info(`${finalName} already exists`);
      } else {
        toast.success(`${finalName} added to ${meta.label}s`);
      }
      setShowQuickAdd(false);
      setQuickInput('');
      onCatalogUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to add ${meta.label}`);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-1">
      <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid={testId || `catalog-${type}`}>
          <SelectValue placeholder={placeholder || `Select ${meta.label}`} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {loading ? (
            <SelectItem value="__loading" disabled>
              <i className="fas fa-spinner fa-spin mr-2"></i>Loading...
            </SelectItem>
          ) : items.length === 0 ? (
            <SelectItem value="__none" disabled>
              No {meta.label}s in catalog
            </SelectItem>
          ) : (
            items.map(it => (
              <SelectItem key={it} value={it}>{it}</SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {canQuickAdd && (
        !showQuickAdd ? (
          <button
            type="button"
            onClick={() => setShowQuickAdd(true)}
            className="text-[11px] text-indigo-600 hover:text-indigo-800 hover:underline inline-flex items-center gap-1"
            data-testid={`${testId || `catalog-${type}`}-quick-add-open`}
          >
            <i className="fas fa-plus text-[9px]"></i>
            Add new {meta.label}
          </button>
        ) : (
          <div className="flex items-center gap-1 pt-1">
            <Input
              value={quickInput}
              onChange={(e) => setQuickInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleQuickAdd(); } }}
              placeholder={`New ${meta.label} name`}
              className="h-8 text-xs"
              autoFocus
              data-testid={`${testId || `catalog-${type}`}-quick-add-input`}
            />
            <Button
              type="button"
              size="sm"
              onClick={handleQuickAdd}
              disabled={adding || !quickInput.trim()}
              className="h-8 px-3 bg-indigo-600 hover:bg-indigo-700"
              data-testid={`${testId || `catalog-${type}`}-quick-add-save`}
            >
              {adding ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => { setShowQuickAdd(false); setQuickInput(''); }}
              className="h-8 px-2"
            >
              <i className="fas fa-times"></i>
            </Button>
          </div>
        )
      )}
    </div>
  );
}
